import {convertMmToM} from '../../../utils/unit';
import type {SketchDimensionQuantityKind} from '../SketchDimensionTypes';
import type {SmartDimensionValueParseResult} from './SketchSmartDimensionTypes';

/**
 * 尺寸值输入解析（阶段 7，VAL-001/VAL-003/VAL-005）。
 *
 * 语义对照 SOLIDWORKS Modify 输入框：
 * 1. 支持算术表达式：`+ - * /`、括号、一元正负号；
 * 2. 数字字面量可带单位后缀（长度 mm/cm/m/in，角度 deg/°/rad），按字面量逐个
 *    换算到默认显示单位（长度毫米、角度度）后做纯数值运算——与 SW 一致，
 *    表达式不做量纲代数（`2*25mm` = 50mm）；
 * 3. 结果为负 → 幅值保留、`negated=true`（负值语义 = 方向翻转，VAL-003），
 *    是否翻转 `branch.directionSign` 由宿主决定；
 * 4. 输出核心单位（米/弧度）；零值与非有限值拒绝。
 */

/** 长度单位 → 毫米系数。 */
const LENGTH_UNIT_TO_MM: Readonly<Record<string, number>> = {
    mm: 1,
    cm: 10,
    m: 1000,
    in: 25.4,
};

/** 角度单位 → 度系数。 */
const ANGLE_UNIT_TO_DEG: Readonly<Record<string, number>> = {
    deg: 1,
    '°': 1,
    rad: 180 / Math.PI,
};

/** 运算符优先级（一元负号最高）。 */
const OPERATOR_PRECEDENCE: Readonly<Record<string, number>> = {
    'u-': 3,
    '*': 2,
    '/': 2,
    '+': 1,
    '-': 1,
};

/**
 * 词法记号模式：数字（可带单位后缀）或运算符/括号。
 * 单位后接字母视为未知单位（如 `5min`），整体报错而不是吞掉前缀。
 */
const TOKEN_PATTERN = /\s*(?:(\d+(?:\.\d+)?|\.\d+)\s*(mm|cm|in|m|deg|rad|°)?(?![a-zA-Z°])|([+\-*/()]))\s*/y;

/**
 * 把表达式切分为记号流：number = 已换算到默认显示单位的字面量，string = 运算符/括号。
 *
 * @returns 记号数组；失败返回错误消息。
 */
function tokenize(rawText: string, quantityKind: SketchDimensionQuantityKind): (number | string)[] | string {
    const unitTable = quantityKind === 'angle' ? ANGLE_UNIT_TO_DEG : LENGTH_UNIT_TO_MM;
    const tokens: (number | string)[] = [];
    let cursor = 0;

    while (cursor < rawText.length) {
        TOKEN_PATTERN.lastIndex = cursor;
        const match = TOKEN_PATTERN.exec(rawText);

        if (!match || match.index !== cursor) {
            return `无法识别的输入：“${rawText.slice(cursor)}”`;
        }
        cursor = TOKEN_PATTERN.lastIndex;

        if (match[1] !== undefined) {
            const unit = match[2];
            const factor = unit === undefined ? 1 : unitTable[unit];

            if (factor === undefined) {
                return quantityKind === 'angle'
                    ? `角度尺寸不支持单位“${unit}”（支持 deg/°/rad）`
                    : `长度尺寸不支持单位“${unit}”（支持 mm/cm/m/in）`;
            }
            tokens.push(Number(match[1]) * factor);
        } else if (match[3] !== undefined) {
            tokens.push(match[3]);
        }
    }

    return tokens;
}

/**
 * 出栈应用一个运算符。
 *
 * @returns 错误消息；成功返回 null。
 */
function applyOperator(values: number[], operator: string): string | null {
    if (operator === 'u-') {
        if (values.length < 1) {
            return '表达式不完整';
        }
        values.push(-values.pop()!);

        return null;
    }

    if (values.length < 2) {
        return '表达式不完整';
    }
    const right = values.pop()!;
    const left = values.pop()!;

    switch (operator) {
        case '+':
            values.push(left + right);
            break;
        case '-':
            values.push(left - right);
            break;
        case '*':
            values.push(left * right);
            break;
        case '/':
            if (right === 0) {
                return '除数不能为 0';
            }
            values.push(left / right);
            break;
        default:
            return `未知运算符“${operator}”`;
    }

    return null;
}

/**
 * 解析 Modify 输入框文本为核心单位尺寸值。
 *
 * @param rawText 用户输入原文（如 `80`、`30mm+2in`、`(45+15)/2deg`、`-25.4`）。
 * @param quantityKind 尺寸量纲（决定默认单位与合法单位集）。
 * @returns 解析结果：成功给出核心单位幅值与负值标记，失败给出中文原因。
 */
export function parseSketchDimensionValueInput(
    rawText: string,
    quantityKind: SketchDimensionQuantityKind,
): SmartDimensionValueParseResult {
    const trimmed = rawText.trim();

    if (trimmed.length === 0) {
        return {ok: false, message: '请输入尺寸值'};
    }

    const tokens = tokenize(trimmed, quantityKind);

    if (typeof tokens === 'string') {
        return {ok: false, message: tokens};
    }

    /** 求值双栈（shunting-yard 即时归约）。 */
    const values: number[] = [];
    const operators: string[] = [];
    /** 前一记号是否处于"期待操作数"位置（决定 +/- 是否一元）。 */
    let expectOperand = true;

    for (const token of tokens) {
        if (typeof token === 'number') {
            if (!expectOperand) {
                return {ok: false, message: '数字之间缺少运算符'};
            }
            values.push(token);
            expectOperand = false;
            continue;
        }

        if (token === '(') {
            if (!expectOperand) {
                return {ok: false, message: '括号前缺少运算符'};
            }
            operators.push(token);
            continue;
        }

        if (token === ')') {
            if (expectOperand) {
                return {ok: false, message: '括号内表达式不完整'};
            }
            let matched = false;

            while (operators.length > 0) {
                const top = operators.pop()!;

                if (top === '(') {
                    matched = true;
                    break;
                }
                const error = applyOperator(values, top);

                if (error !== null) {
                    return {ok: false, message: error};
                }
            }
            if (!matched) {
                return {ok: false, message: '括号不匹配'};
            }
            continue;
        }

        // 运算符：操作数位置上的 +/- 归一为一元（+ 直接忽略，- 记 u-）。
        let operator = token;

        if (expectOperand) {
            if (token === '+') {
                continue;
            }
            if (token === '-') {
                operator = 'u-';
            } else {
                return {ok: false, message: '运算符位置不合法'};
            }
        }

        const precedence = OPERATOR_PRECEDENCE[operator]!;

        // 一元负号右结合（-- 叠加），二元左结合。
        while (operators.length > 0) {
            const top = operators[operators.length - 1]!;

            if (top === '(') {
                break;
            }
            const topPrecedence = OPERATOR_PRECEDENCE[top]!;

            if (topPrecedence > precedence || (topPrecedence === precedence && operator !== 'u-')) {
                const error = applyOperator(values, operators.pop()!);

                if (error !== null) {
                    return {ok: false, message: error};
                }
            } else {
                break;
            }
        }
        operators.push(operator);
        expectOperand = true;
    }

    if (expectOperand) {
        return {ok: false, message: '表达式不完整'};
    }

    while (operators.length > 0) {
        const top = operators.pop()!;

        if (top === '(') {
            return {ok: false, message: '括号不匹配'};
        }
        const error = applyOperator(values, top);

        if (error !== null) {
            return {ok: false, message: error};
        }
    }

    if (values.length !== 1) {
        return {ok: false, message: '表达式不完整'};
    }

    const displayValue = values[0]!;

    if (!Number.isFinite(displayValue)) {
        return {ok: false, message: '计算结果不是有效数值'};
    }
    if (displayValue === 0) {
        return {ok: false, message: '尺寸值不能为 0'};
    }

    const magnitude = Math.abs(displayValue);
    const coreValue = quantityKind === 'angle'
        ? (magnitude * Math.PI) / 180
        : convertMmToM(magnitude);

    return {ok: true, coreValue, negated: displayValue < 0};
}
